import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import BlueSpin from "../assets/images/BlueSpin.png";
import "../App.css";
import "./Camera.css";
import "./CardScanner.css";
import paintCanvasImage from "../utils/paintCanvasImage";
import { CanvasesRefType, CardBoundingBoxType, CardContentType, VideoSizeType, VisionLoadingType } from "../types";
import { startVideoStream } from "../utils/StartStopVideo";
import { CalculateBoundingBox, CropAndCopyCanvas, DeskewCanvas } from "../utils/BoundingBoxCalculations";
import CardOutlineSvg from "../assets/svgs/CardOutlineSvg";
import ScanCanvas from "../utils/ScanCanvas";
import { useUser } from "../hooks/useUser";
import mergeCardObjects from "../utils/MergeCardObjects";
import Cropper, { Area } from "react-easy-crop";

const USE_VISION = true;
const LANDSCAPE_RATIO = 91 / 55;
const PORTRAIT_RATIO = 55 / 91;


type CardScannerPropsType = {
  canvases: CanvasesRefType;
  unsavedCanvas: { front: boolean, back: boolean };
  setUnsavedCanvas: React.Dispatch<React.SetStateAction<{ front: boolean; back: boolean; }>>;
  isCaptureMode: boolean;
  setIsCaptureMode: (isCaptureMode: boolean) => void;
  isOwner: boolean;
  isUploadOnly: boolean;
  isVisionLoading: { front: boolean, back: boolean };
  setIsVisionLoading: React.Dispatch<React.SetStateAction<VisionLoadingType>>;
  setScannedBusinessCard: React.Dispatch<React.SetStateAction<{ front: any; back: any; merged: any; } | null>>;
};

const CardScanner: React.FC<CardScannerPropsType> = ({ canvases, unsavedCanvas, setUnsavedCanvas, isCaptureMode, setIsCaptureMode, isOwner, isUploadOnly, isVisionLoading, setIsVisionLoading, setScannedBusinessCard }) => {
  const { cardKeyParam } = useParams<{ cardKeyParam?: string }>();
  const cardKey = Number(cardKeyParam) ? Number(cardKeyParam) : null;

  const user = useUser();
  const { userKey, userToken, isDarkMode, /*loading: userLoading*/ } = user;

  const [displayCamera, setDisplayCamera] = useState(false);
  const [isFileHovering, setIsFileHovering] = useState({ page: false, front: false, back: false });
  const [uploadDone, setUploadDone] = useState({ front: false, back: false });

  const [isHorizontalCard, setIsHorizontalCard] = useState(true);
  const [isCapturingFront, setIsCapturingFront] = useState(true);

  const fullCameraRef = useRef<HTMLDivElement>(null);
  const cardSideToggleRef = useRef<HTMLDivElement>(null);
  const cardOrientationToggleRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSize, setVideoSize] = useState<VideoSizeType | null>(null);
  const [cardBoundingBox, setCardBoundingBox] = useState<CardBoundingBoxType | null>(null);
  const [cameraState, setCameraState] = useState<"untouched" | "initializing" | "ready" | "capturing" | "paused" | "error">("untouched");
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const cancelActivateRef = useRef<(() => void) | null>(null);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppingFace, setCroppingFace] = useState<"front" | "back" | null>(null);
  const [isCroppingLandscape, setIsCroppingLandscape] = useState(true);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    return () => {
      stopCamera(); // cleanup on unmount
    };
  }, []);

  useEffect(() => {
    if (selectedImage) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [selectedImage]);

  // Draws, scales, and crops current frame from the video feed to the ouput canvas
  const captureAndPaint = async () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    console.log("Capture and show");
    if (!videoSize || !cardBoundingBox || !videoRef.current) {
      console.error("Video feed not ready");
      setDisplayCamera(false);
      stopCamera();
      return;
    }

    const outputScannerCanvas = isCapturingFront ? canvases.scanner.front.current : canvases.scanner.back.current;
    const outputViewerCanvas = isCapturingFront ? canvases.viewer.front.current : canvases.viewer.back.current;
    if (!outputScannerCanvas) {
      console.error("Destination canvas not ready");
      setDisplayCamera(false);
      stopCamera();
      return;
    }

    CropAndCopyCanvas(videoRef.current, outputScannerCanvas, isHorizontalCard, cardBoundingBox);
    DeskewCanvas(outputScannerCanvas);
    

    if (outputViewerCanvas) {
      CopyCanvas(outputScannerCanvas, outputViewerCanvas, isHorizontalCard);
    } else {
      console.error("Viewer canvas not ready");
    }

    setUnsavedCanvas((prev) => { return isCapturingFront ? { ...prev, front: true } : { ...prev, back: true }; });

    setDisplayCamera(false);
    stopCamera();

    if (USE_VISION) {
      ReadCard(outputScannerCanvas);
    }
  }

  function CopyCanvas(inputCanvas: HTMLCanvasElement, outputCanvas: HTMLCanvasElement, isHorizontalCard: boolean) {
    if (!inputCanvas || !outputCanvas) {
      console.error("One or both canvas elements are missing.");
      return;
    }

    const outputCtx = outputCanvas.getContext("2d");
    if (!outputCtx) {
      console.error("Failed to get 2D context of the output canvas.");
      return;
    }

    // Set output canvas dimensions based on isHorizontalCard
    if (isHorizontalCard) {
      outputCanvas.width = 853;
      outputCanvas.height = 512;
    } else {
      outputCanvas.width = 512;
      outputCanvas.height = 853;
    }

    // Copy the entire content from inputCanvas to outputCanvas
    outputCtx.drawImage(inputCanvas, 0, 0, outputCanvas.width, outputCanvas.height);
  }

  const ReadCard = async (canvas: HTMLCanvasElement) => {
    setIsVisionLoading((prev: VisionLoadingType) => {
      return isCapturingFront ? { ...prev, front: true } : { ...prev, back: true };
    });

    const newScannedData: CardContentType = await ScanCanvas(userKey, userToken, canvas); // Using Open AI Vision API

    // We want to store if the data is from the front or back of the card
    for (const key in newScannedData) {
      const dataArray = newScannedData[key as keyof CardContentType];
      if (Array.isArray(dataArray)) {
        newScannedData[key as keyof CardContentType] = dataArray.map(datum => ({
          ...datum,
          face: isCapturingFront ? "front" : "back"
        }));
      }
    }

    setScannedBusinessCard((prev) => {
      return mergeCardObjects({ previousData: prev, newData: newScannedData, newDataFace: isCapturingFront ? "front" : "back" });
    });

    setIsVisionLoading(prev => {
      return isCapturingFront ? { ...prev, front: false } : { ...prev, back: false };
    });
  }

  useEffect(() => {
    if (cardSideToggleRef.current) {
      cardSideToggleRef.current.style.left = isCapturingFront ? "0" : "50%";
    }
  }, [isCapturingFront]);

  useEffect(() => {
    if (cardOrientationToggleRef.current) {
      cardOrientationToggleRef.current.style.left = isHorizontalCard ? "0" : "50%";
    }
  }, [isHorizontalCard]);

  const imageUploadInput: React.FC<{ face: "front" | "back" }> = ({ face }) => {
    // If we came to this page specifically to upload images, show the hint box unless an upload has already been done
    // Also, if the user is hovering with a file, show the hint box always
    const displayUploadBox = (isUploadOnly && !uploadDone[face]) || isFileHovering.page;
    const highlightUploadBox = isFileHovering[face];
    return (
      <div className={`image-upload ${highlightUploadBox ? "image-upload-hover" : ""}`}
        style={{ opacity: displayUploadBox ? 1 : 0 }}
        onClick={() => {
          const inputElement = document.getElementById(`fileInput${face}`) as HTMLInputElement;
          if (inputElement) inputElement.click();
        }}
        onDragOver={(e) => {
          setIsFileHovering(prev => ({ ...prev, [face]: true }))
          e.preventDefault();
        }}
        onDragLeave={(e) => {
          setIsFileHovering(prev => ({ ...prev, [face]: false }))
        }}
        onDrop={(e) => {
          console.log("Drop event");
          e.preventDefault();
          setIsFileHovering(prev => ({ ...prev, page: false, [face]: false }))
          const file = e.dataTransfer.files[0];
        }}
      >
        {/*<span>Click or Drag & Drop Upload to {face}</span>*/}
        <span>クリックまたはドラッグ＆ドロップで<br />「{face === "front" ? "前面" : "裏面"}」にアップロードします</span>
        <input
          type="file"
          id={`fileInput${face}`}
          accept="image/jpeg, image/png"
          style={{ display: "none" }}
          onChange={(e) => {
            if (!e.target.files) return;
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
              setSelectedImage(reader.result as string);
              setCroppingFace(face); // Set whether it's front or back
            };
          }}
        />
      </div>
    );
  }

  const svgOutlineWidth = (isHorizontalCard ? cardBoundingBox?.cardMajorAxis || 0 : cardBoundingBox?.cardMinorAxis || 0);
  const svgOutlineHeight = (isHorizontalCard ? cardBoundingBox?.cardMinorAxis || 0 : cardBoundingBox?.cardMajorAxis || 0);

  const faceControl = (
    <div>
      <span className="full-capture-controls-label">名刺の側</span>
      <div className="drag-toggle" onClick={() => setIsCapturingFront(!isCapturingFront)}>
        <div className="drag-handle" ref={cardSideToggleRef} />
        <span className={isCapturingFront ? "drag-span" : "drag-span drag-off"}>名刺の表側</span>
        <span className={!isCapturingFront ? "drag-span" : "drag-span drag-off"}>名刺の裏面</span>
      </div>
    </div>
  );

  const orientationControl = (
    <div>
      <span className="full-capture-controls-label">名刺の向き</span>
      <div className="drag-toggle" onClick={() => setIsHorizontalCard(!isHorizontalCard)}>
        <div className="drag-handle" ref={cardOrientationToggleRef} />
        <span className={isHorizontalCard ? "drag-span" : "drag-span drag-off"}>横長名刺</span>
        <span className={!isHorizontalCard ? "drag-span" : "drag-span drag-off"}>縦長名刺</span>
      </div>
    </div>
  );

  const fullCamera = (
    <div ref={fullCameraRef} className="full-camera-container" style={{ display: displayCamera ? undefined : "none" }}>
      {/* Video feed */}
      <div className="full-video-container">
        <div
          className="full-video-outline"
          style={{ width: `${cardBoundingBox?.cardWidthPercent}%`, height: `${cardBoundingBox?.cardHeightPercent}%` }}
        >
          <CardOutlineSvg width={svgOutlineWidth} height={svgOutlineHeight} />
        </div>
        <video ref={videoRef} playsInline autoPlay muted className="full-video-feed" />
      </div>
      <div className="full-capture-container">
        <div>
          <div className="full-capture-controls">
            {faceControl}{orientationControl}
          </div>
        </div>
        <div onClick={captureAndPaint} className="capture-button"></div>
      </div>
      <button onClick={() => {
        setDisplayCamera(false);
        stopCamera();
        if (document.fullscreenElement) document.exitFullscreen?.();
        }} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-transparent">
        <span className="absolute w-6 h-1 bg-white rotate-45 rounded"></span>
        <span className="absolute w-6 h-1 bg-white -rotate-45 rounded"></span>
      </button>
      <span className="full-camera-state">{cameraState}</span>
    </div>
  );

  const unsavedWarning = <span className="unsavedWarning">(保存されません)</span>

  const frontScanning = isVisionLoading.front;
  const backScanning = isVisionLoading.back;
  const somethingScanning = frontScanning || backScanning;

  const cardFrontCanvas = (
    <div>
      <span className="block mb-2">
        前面{unsavedCanvas.front ? unsavedWarning : null}
      </span>
      <div className="relative w-full aspect-[5/3] bg-gray-100 border-2 border-gray-300 border-dashed">
        <canvas
          id="frontCanvas"
          ref={canvases.scanner.front}
          className={`absolute inset-0 w-full h-full ${unsavedCanvas.front ? "card-canvas-container-unsaved" : ""}`}
        ></canvas>
        <div
          style={{ backgroundImage: `url(${BlueSpin})`, display: isVisionLoading.front ? undefined : "none" }}
          className="card-spinner absolute inset-0"
        ></div>
        {imageUploadInput({ face: "front" })}
        {!selectedImage && (
          <div className="flex items-center justify-center h-full w-full text-gray-400 text-lg">
            <span>クリックして前面をアップロードする</span>
          </div>
        )}
      </div>
    </div>
  );

  const cardBackCanvas = (
    <div>
      <span className="block mb-2">
       裏面{unsavedCanvas.back ? unsavedWarning : null}
      </span>
      <div className="relative w-full aspect-[5/3] bg-gray-100 border-2 border-gray-300 border-dashed">
        <canvas
          id="backCanvas"
          ref={canvases.scanner.back}
          className={`absolute inset-0 w-full h-full ${unsavedCanvas.back ? "card-canvas-container-unsaved" : ""}`}
        ></canvas>
        <div
          style={{ backgroundImage: `url(${BlueSpin})`, display: isVisionLoading.back ? undefined : "none" }}
          className="card-spinner absolute inset-0"
        ></div>
        {imageUploadInput({ face: "back" })}
        {!selectedImage && (
          <div className="flex items-center justify-center h-full w-full text-gray-400 text-lg">
            <span>クリックして裏面をアップロードする</span>
          </div>
        )}
      </div>
    </div>
  );

  useEffect(() => {
    if (!cardKey) return;
    paintCanvasImage(cardKey, "front", canvases.scanner);
    paintCanvasImage(cardKey, "back", canvases.scanner);
  }, [cardKey]);

  // Calculate the bounding box for the card (used to show the dashed line, and to crop the image)
  useEffect(() => {
    if (!videoSize) return;
    setCardBoundingBox(CalculateBoundingBox(videoSize, isHorizontalCard));
  }, [videoSize, isHorizontalCard]);

  const openFullCamera = async () => {
    if (cameraState === "untouched") {
      setCameraState("initializing");
      const cancel = await activateCamera();
      cancelActivateRef.current = cancel || null;
    }
    setDisplayCamera(true);

    if (fullCameraRef.current) {
      fullCameraRef.current.requestFullscreen?.().catch(err => {
        console.error("Failed to enable full-screen mode:", err);
      });
    }
  }

  const activateCamera = async () => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      setCameraState("error");
      return;
    }

    const handleMetadataLoaded = () => {
      setVideoSize({ width: videoElement.videoWidth, height: videoElement.videoHeight });
      setCameraState("ready");
    };

    videoElement.addEventListener("loadedmetadata", handleMetadataLoaded);

    let isCancelled = false;

    try {
      const stream = await startVideoStream(videoRef);

      if (isCancelled) {
        stream?.getTracks().forEach((track) => track.stop());
        setCameraState("untouched");
        return;
      }

      if (stream) {
        setMediaStream(stream); // store stream for cleanup
      } else {
        setCameraState("untouched");
        //alert("カメラが起動できませんでした。");
      }
    } catch (error) {
      console.error("Error starting camera:", error);
      alert("カメラが起動できませんでした。");
      setCameraState("untouched");
    }

    return () => {
      isCancelled = true;
      videoElement.removeEventListener("loadedmetadata", handleMetadataLoaded);
  
      // Don't use stopCamera() here — endless loop
      if (videoElement.srcObject instanceof MediaStream) {
        videoElement.srcObject.getTracks().forEach(track => track.stop());
        videoElement.srcObject = null;
      }
  
      setMediaStream(null);
    };
  };

  // Should prevent endless loops, which I did by putting stopCamera() in the cleanup function of activateCamera()
  const isStoppingRef = useRef(false);
  const stopCamera = () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    if (cancelActivateRef.current) {
      cancelActivateRef.current(); // cancel pending camera start
      cancelActivateRef.current = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }

    setCameraState("untouched");
    isStoppingRef.current = false;
  };

  const handleCropComplete = async (deskew: boolean) => {
    console.log(`Crop time, cropping face: ${croppingFace}, croppedAreaPixels:`, !croppedAreaPixels ? "false" : "true", `selectedImage:`, !selectedImage ? "false" : "true");
    if (!croppedAreaPixels || !selectedImage || !croppingFace) return;

    const image = new Image();
    image.src = selectedImage;
    image.onload = () => {
      const outputScannerCanvas = croppingFace === "front" ? canvases.scanner.front.current : canvases.scanner.back.current;
      if (!outputScannerCanvas) return;

      const ctx = outputScannerCanvas.getContext("2d");
      if (!ctx) return;

      let { width, height } = croppedAreaPixels;
      const aspectRatio = width / height;

      // Resize based on aspect ratio
      if (aspectRatio > 1) {
        // Landscape
        if (height > 512) {
          height = 512;
          width = Math.min(853, height * aspectRatio);
        } else if (width > 853) {
          width = 853;
          height = Math.min(512, width / aspectRatio);
        }
      } else {
        // Portrait
        if (width > 512) {
          width = 512;
          height = Math.min(853, width / aspectRatio);
        } else if (height > 853) {
          height = 853;
          width = Math.min(512, height * aspectRatio);
        }
      }

      // Create a temporary canvas for resizing
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext("2d");
      if (!tempCtx) return;

      tempCtx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        width,
        height
      );

      // Copy resized image to final canvas
      outputScannerCanvas.width = width;
      outputScannerCanvas.height = height;
      ctx.drawImage(tempCanvas, 0, 0);

      // Mark as unsaved
      setUnsavedCanvas((prev) => ({
        ...prev,
        [croppingFace]: true,
      }));

      // Clear cropping state
      setSelectedImage(null);
      setCroppingFace(null);
      if (deskew) {
        DeskewCanvas(outputScannerCanvas);
      }

      const outputViewerCanvas = croppingFace === "front" ? canvases.viewer.front.current : canvases.viewer.back.current;
      if (outputViewerCanvas) {
        CopyCanvas(outputScannerCanvas, outputViewerCanvas, isHorizontalCard);
      } else {
        console.error("Viewer canvas not ready");
      }

      setUnsavedCanvas((prev) => { return croppingFace === "front" ? { ...prev, front: true } : { ...prev, back: true }; });

      if (USE_VISION) {
        ReadCard(outputScannerCanvas);
      }

    };
  };

  useEffect(() => {
    console.log("unsavedCanvas state:", unsavedCanvas);
  }, [unsavedCanvas]);

  return (
    <div>{fullCamera}
      <div className="drop-shadow-md bg-white rounded-md p-3 w-full" style={{ display: isCaptureMode ? undefined : "none" }}>
        <div className="flex flex-col md:flex-row justify-start w-full gap-2">
          <div className="btn btn-primary px-8 w-full md:w-fit" style={{ color: somethingScanning ? "#68a" : undefined }} onClick={() => { if (!somethingScanning) setIsCaptureMode(false) }}><span>カードの内容を表示へ</span>
    
          </div>
          <div className="btn btn-primary px-8 w-full md:w-fit" onClick={openFullCamera}>写真を撮る</div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 w-full max-w-screen-md mx-auto my-8">
          <div className="w-full md:w-1/2">
            {cardFrontCanvas}
          </div>
          <div className="w-full md:w-1/2">
            {cardBackCanvas}
          </div>
        </div>
          <dialog ref={dialogRef} className="dark-dialog rounded-md p-4 w-[90vw] max-w-4xl bg-white">
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <button className="btn btn-primary" onClick={() => handleCropComplete(false)}>スキャンする</button>
              <button className="btn btn-primary" onClick={() => handleCropComplete(true)}>傾き補正付きでスキャンする</button>
              <button className="btn btn-primary" onClick={() => { setSelectedImage(null); setCroppingFace(null); }}>キャンセル</button>
            </div>
            <div className="relative w-full h-[60vh]">
              <Cropper
                image={selectedImage || undefined}
                crop={crop}
                zoom={zoom}
                zoomSpeed={0.1}
                aspect={isCroppingLandscape ? LANDSCAPE_RATIO : PORTRAIT_RATIO}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
              />
            </div>
          </dialog>
      </div>
    </div>
  );
}

export default CardScanner;
